import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const PreviewModal = ({ template, coin, customFields, filterFields, onClose, onSave }) => {
  const [mounted, setMounted] = useState(false);
  const [fieldStyles, setFieldStyles] = useState(() => {
    return template.coin_fields.map((field, index) => ({
      id: field.id,
      text_align: field.text_align || 'left',
      font_size_pt: field.font_size_pt || 12,
      line_height_em: field.line_height_em || 1.5,
      x: 0,
      y: index * 30, // Initial vertical spacing
      width: '100%',
      isDragging: false
    }));
  });
  const [selectedFieldId, setSelectedFieldId] = useState(null);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const getFieldValue = (fieldId) => {
    if (fieldId.startsWith('custom_field_')) {
      const customField = customFields.find(
        f => `custom_field_${f.id}` === fieldId
      );
      return customField ? coin[`custom_field_${customField.id}`] || '-' : '-';
    }

    // Handle special cases first
    if (fieldId === 'certificationNumber') return coin['certification_number'] || '-';
    if (fieldId === 'certificationServiceId') return coin['certification_service_id'] || '-';
    if (fieldId === 'mintMark') return coin['mint_mark'] || '-';
    if (fieldId === 'currentPopHigher') return coin['current_pop_higher'] || '-';
    if (fieldId === 'currentPopulation') return coin['current_population'] || '-';
    if (fieldId === 'dieVariety') return coin['die_variety'] || '-';
    if (fieldId === 'listPrice') return coin['list_price'] || '-';
    if (fieldId === 'minorVariety') return coin['minor_variety'] || '-';
    if (fieldId === 'pcgsNumber') return coin['pcgs_number'] || '-';
    if (fieldId === 'popHigherWhenBought') return coin['pop_higher_when_bought'] || '-';
    if (fieldId === 'populationWhenBought') return coin['population_when_bought'] || '-';
    if (fieldId === 'priceGuideValue') return coin['price_guide_value'] || '-';
    if (fieldId === 'purchaseDate') return coin['purchase_date'] || '-';
    if (fieldId === 'purchasePrice') return coin['purchase_price'] || '-';
    if (fieldId === 'referenceNumber') return coin['reference_number'] || '-';
    if (fieldId === 'shippingCost') return coin['shipping_cost'] || '-';
    if (fieldId === 'storageLocation') return coin['storage_location'] || '-';
    if (fieldId === 'totalCost') return coin['total_cost'] || '-';

    // For other fields, convert camelCase to snake_case
    const snakeCaseField = fieldId.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    return coin[snakeCaseField] || '-';
  };

  const getFieldLabel = (fieldId) => {
    if (fieldId.startsWith('custom_field_')) {
      const customField = customFields.find(
        f => `custom_field_${f.id}` === fieldId
      );
      return customField?.name;
    }
    return filterFields.find(f => f.id === fieldId)?.label;
  };

  const updateFieldStyle = (fieldId, property, value) => {
    setFieldStyles(prev => 
      prev.map(style => 
        style.id === fieldId 
          ? { ...style, [property]: value }
          : style
      )
    );
  };

  const getSelectedStyle = () => {
    return fieldStyles.find(style => style.id === selectedFieldId) || {};
  };

  const modal = (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Label Preview: {template.name}</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Toolbar */}
              <div className="flex items-center justify-between p-2 border-b">
                {/* Left section: Text alignment */}
                <div className="flex items-center space-x-1 border-r pr-4">
                  <button
                    onClick={() => selectedFieldId && updateFieldStyle(selectedFieldId, 'text_align', 'left')}
                    className={`p-1 rounded ${getSelectedStyle().text_align === 'left' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                    disabled={!selectedFieldId}
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 4h16v2H2V4zm0 5h10v2H2V9zm0 5h16v2H2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => selectedFieldId && updateFieldStyle(selectedFieldId, 'text_align', 'center')}
                    className={`p-1 rounded ${getSelectedStyle().text_align === 'center' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                    disabled={!selectedFieldId}
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 4h16v2H2V4zm3 5h10v2H5V9zm-3 5h16v2H2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => selectedFieldId && updateFieldStyle(selectedFieldId, 'text_align', 'right')}
                    className={`p-1 rounded ${getSelectedStyle().text_align === 'right' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                    disabled={!selectedFieldId}
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 4h16v2H2V4zm6 5h10v2H8V9zm-6 5h16v2H2v-2z" />
                    </svg>
                  </button>
                </div>

                {/* Middle section: Font controls */}
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-600">Font Size</label>
                    <input
                      type="number"
                      value={getSelectedStyle().font_size_pt || 12}
                      onChange={(e) => selectedFieldId && updateFieldStyle(selectedFieldId, 'font_size_pt', parseFloat(e.target.value))}
                      min="6"
                      max="72"
                      step="0.5"
                      className="w-16 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      disabled={!selectedFieldId}
                    />
                    <span className="text-sm text-gray-600">pt</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-600">Line Height</label>
                    <input
                      type="number"
                      value={getSelectedStyle().line_height_em || 1.5}
                      onChange={(e) => selectedFieldId && updateFieldStyle(selectedFieldId, 'line_height_em', parseFloat(e.target.value))}
                      min="1"
                      max="3"
                      step="0.1"
                      className="w-16 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      disabled={!selectedFieldId}
                    />
                    <span className="text-sm text-gray-600">em</span>
                  </div>
                </div>

                {/* Right section: Position and width controls */}
                <div className="flex items-center space-x-4 border-l pl-4">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-600">Width</label>
                    <select
                      value={getSelectedStyle().width || '100%'}
                      onChange={(e) => selectedFieldId && updateFieldStyle(selectedFieldId, 'width', e.target.value)}
                      className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      disabled={!selectedFieldId}
                    >
                      <option value="100%">Full</option>
                      <option value="50%">Half</option>
                      <option value="33%">Third</option>
                      <option value="25%">Quarter</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        if (selectedFieldId) {
                          setFieldStyles(prev => prev.map(style => 
                            style.id === selectedFieldId 
                              ? { ...style, y: Math.max(0, style.y - 10) }
                              : style
                          ));
                        }
                      }}
                      className="p-1 rounded hover:bg-gray-50"
                      disabled={!selectedFieldId}
                    >
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M5 10l5-5 5 5H5z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        if (selectedFieldId) {
                          setFieldStyles(prev => prev.map(style => 
                            style.id === selectedFieldId 
                              ? { ...style, y: style.y + 10 }
                              : style
                          ));
                        }
                      }}
                      className="p-1 rounded hover:bg-gray-50"
                      disabled={!selectedFieldId}
                    >
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M15 10l-5 5-5-5h10z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        if (selectedFieldId) {
                          setFieldStyles(prev => prev.map(style => 
                            style.id === selectedFieldId 
                              ? { ...style, x: Math.max(0, style.x - 10) }
                              : style
                          ));
                        }
                      }}
                      className="p-1 rounded hover:bg-gray-50"
                      disabled={!selectedFieldId}
                    >
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 5l-5 5h10l-5-5z" transform="rotate(-90 10 10)" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        if (selectedFieldId) {
                          setFieldStyles(prev => prev.map(style => 
                            style.id === selectedFieldId 
                              ? { ...style, x: style.x + 10 }
                              : style
                          ));
                        }
                      }}
                      className="p-1 rounded hover:bg-gray-50"
                      disabled={!selectedFieldId}
                    >
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 15l5-5H5l5 5z" transform="rotate(90 10 10)" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Preview Area */}
              <div 
                className="border rounded-lg p-4 mx-auto relative"
                style={{
                  width: `${template.width_mm * 3.78}px`,
                  height: `${template.height_mm * 3.78}px`,
                  backgroundColor: '#fff'
                }}
              >
                {fieldStyles.map((style, index) => (
                  <div 
                    key={`${style.id}-${index}`}
                    onClick={() => setSelectedFieldId(style.id)}
                    style={{
                      position: 'absolute',
                      left: `${style.x}px`,
                      top: `${style.y}px`,
                      width: style.width,
                      textAlign: style.text_align,
                      fontSize: `${style.font_size_pt}pt`,
                      lineHeight: `${style.line_height_em}em`,
                      padding: '2px 4px',
                      cursor: 'move',
                      backgroundColor: selectedFieldId === style.id ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                      border: selectedFieldId === style.id ? '1px dashed #3b82f6' : '1px solid transparent',
                      userSelect: 'none',
                      zIndex: selectedFieldId === style.id ? 10 : 1
                    }}
                    draggable={true}
                    onDragStart={(e) => {
                      setSelectedFieldId(style.id);
                      setFieldStyles(prev => prev.map(s => 
                        s.id === style.id ? { ...s, isDragging: true } : s
                      ));
                      e.dataTransfer.setData('text/plain', style.id);
                    }}
                    onDragEnd={() => {
                      setFieldStyles(prev => prev.map(s => 
                        s.id === style.id ? { ...s, isDragging: false } : s
                      ));
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      const rect = e.currentTarget.parentElement.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const y = e.clientY - rect.top;
                      
                      if (selectedFieldId) {
                        setFieldStyles(prev => prev.map(s => 
                          s.id === selectedFieldId 
                            ? { ...s, x: Math.max(0, x), y: Math.max(0, y) }
                            : s
                        ));
                      }
                    }}
                  >
                    {getFieldValue(style.id?.id || style.id)}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
              <button
                onClick={() => onSave?.(fieldStyles)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modal, document.body);
};

export default PreviewModal;
